import React from "react";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { ShoppingCart, Package, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { F } from "@/components/shared/F";
import { FormFieldError } from "@/lib/form/FormFieldError";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { formatNumber } from "@/lib/utils";

import { toVnDateString } from "@apv-erp/status";
export interface PurchasingFormData {
  /** Có khi sửa đơn đã lưu (phân biệt tạo mới / sửa cho nút Lưu nháp) */
  id?: string;
  vendorId: string;
  orderDate: string;
  expectedDate: string;
  receivedDate: string;
  status: string;
  notes: string;
  items: Array<{
    id?: string;
    notes?: string;
    materialId: string;
    productionOrderId?: string;
    quantity: number;
    unitPrice: number;
    totalAmount: number;
    materialPlanId?: string;
  }>;
}

const formSchema = z.object({
  vendorId: z.string().min(1, "Vui lòng chọn nhà cung cấp"),
  orderDate: z.string().min(1, "Vui lòng chọn ngày đặt"),
  expectedDate: z.string(),
  receivedDate: z.string(),
  status: z.string(),
  notes: z.string(),
  items: z.array(z.object({
    id: z.string().optional(),
    notes: z.string().optional(),
    materialId: z.string().min(1, "Chọn vật tư"),
    productionOrderId: z.string().optional(),
    quantity: z.number().min(0.01, "SL phải > 0"),
    unitPrice: z.number().min(0, "Giá phải >= 0"),
    totalAmount: z.number(),
    materialPlanId: z.string().optional(),
  })).min(1, "Đơn hàng phải có ít nhất 1 vật tư"),
});

interface PurchasingFormProps {
  defaultValues?: Partial<PurchasingFormData>;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function PurchasingForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu Đơn Mua Hàng",
}: PurchasingFormProps) {
  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1)
  const currentStatus = (defaultValues as any)?.id ? ((defaultValues as any)?.status as string | undefined) : undefined;
  const [isDraftSaving, setIsDraftSaving] = React.useState(false);
  const buildPayload = (value: any, saveMode: "DRAFT" | "SUBMIT") => ({
    vendorId: value.vendorId,
    orderDate: new Date(value.orderDate),
    expectedDate: value.expectedDate ? new Date(value.expectedDate) : null,
    notes: value.notes,
    saveMode,
    // Backend tự tính totalAmount từ items
    items: (value.items || [])
      .filter((item: any) => saveMode === "SUBMIT" || item.materialId)
      .map((item: any) => ({
        ...(item.id ? { id: item.id } : {}),
        notes: item.notes || null,
        materialId: item.materialId,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        productionOrderId: item.productionOrderId || null,
        materialPlanningId: item.materialPlanId || null,
      })),
  });

  const form = useForm({
    defaultValues: {
      vendorId: defaultValues?.vendorId || "",
      orderDate:
        defaultValues?.orderDate || toVnDateString(),
      expectedDate: defaultValues?.expectedDate || "",
      receivedDate: defaultValues?.receivedDate || "",
      status: defaultValues?.status || "DRAFT",
      notes: defaultValues?.notes || "",
      items: defaultValues?.items && defaultValues.items.length > 0
        ? defaultValues.items
        : [{ materialId: "", quantity: 1, unitPrice: 0, totalAmount: 0 }],
    },
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      // Map to correct format for backend createPurchaseOrderSchema
      await onSubmit(buildPayload(value, "SUBMIT"));
    },
  });

  // Lưu nháp: ghi tạm (chỉ cần nhà cung cấp), không kiểm tra đủ thông tin
  const handleSaveDraft = async () => {
    const value = form.state.values as any;
    if (!value.vendorId) {
      toast.error("Chọn nhà cung cấp trước khi lưu nháp");
      return;
    }
    setIsDraftSaving(true);
    try {
      await onSubmit(buildPayload(value, "DRAFT"));
    } finally {
      setIsDraftSaving(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-slate-500" /> Thông tin đơn
            mua
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <form.Field name="vendorId">
            {(field: any) => (
              <div className="max-w-md">
                <SharedCombobox
                  type="vendor"
                  value={field.state.value || ""}
                  valueLabel={(defaultValues as any)?.vendor?.name || (defaultValues as any)?.vendor?.shortName}
                  onChange={(id) => field.handleChange(id)}
                  label="Nhà cung cấp"
                  required
                  placeholder="Chọn NCC..."
                />
                <FormFieldError field={field} />
              </div>
            )}
          </form.Field>

          {/* Items Array */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-700">Danh sách vật tư</Label>
            
            <form.Field name="items">
              {(field: any) => (
                <div className="border rounded-md overflow-hidden bg-white">
                  <div className="grid grid-cols-12 gap-2 bg-slate-50 p-2 text-xs font-semibold text-slate-600 border-b">
                    <div className="col-span-4">Vật tư</div>
                    <div className="col-span-2">LSX (tuỳ chọn)</div>
                    <div className="col-span-2 text-right">Số lượng</div>
                    <div className="col-span-2 text-right">Đơn giá</div>
                    <div className="col-span-1 text-right">Thành tiền</div>
                    <div className="col-span-1 text-center">Xoá</div>
                  </div>
                  
                  {field.state.value.map((_: any, index: number) => (
                    <div key={index} className="grid grid-cols-12 gap-2 p-2 items-center border-b last:border-b-0">
                      {/* Material Combobox */}
                      <div className="col-span-4">
                        <form.Field name={`items[${index}].materialId`}>
                          {(subField: any) => (
                            <SharedCombobox
                              type="material"
                              value={subField.state.value || ""}
                              valueLabel={(defaultValues as any)?.items?.[index]?.materialName}
                              params={{ preferVendorId: form.getFieldValue("vendorId") || undefined }} /* vật tư của NCC lên đầu, vẫn chọn được vật tư khác (FB-AK4ZVF) */
                              onChange={(id, opt) => {
                                subField.handleChange(id);
                                if (opt?.original) {
                                  const orig = opt.original;
                                  if (orig.unitPrice) {
                                    const price = Number(orig.unitPrice);
                                    form.setFieldValue(`items[${index}].unitPrice`, price);
                                    const qty = Number(form.getFieldValue(`items[${index}].quantity`)) || 1;
                                    form.setFieldValue(`items[${index}].totalAmount`, qty * price);
                                  }
                                }
                              }}
                              placeholder="Chọn vật tư..."
                            />
                          )}
                        </form.Field>
                      </div>

                      {/* Production Order Combobox */}
                      <div className="col-span-2">
                        <form.Field name={`items[${index}].productionOrderId`}>
                          {(subField: any) => (
                            <SharedCombobox
                              type="productionOrder"
                              value={subField.state.value || ""}
                              valueLabel={(defaultValues as any)?.items?.[index]?.lsxId}
                              onChange={(id) => subField.handleChange(id)}
                              placeholder="Chọn LSX..."
                            />
                          )}
                        </form.Field>
                      </div>

                      {/* Quantity */}
                      <div className="col-span-2">
                        <form.Field name={`items[${index}].quantity`}>
                          {(subField: any) => (
                            <input
                              type="number"
                              className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 text-right"
                              value={subField.state.value}
                              onChange={(e) => {
                                const qty = Number(e.target.value);
                                subField.handleChange(qty);
                                const price = Number(form.getFieldValue(`items[${index}].unitPrice`));
                                form.setFieldValue(`items[${index}].totalAmount`, qty * price);
                              }}
                            />
                          )}
                        </form.Field>
                      </div>

                      {/* Unit Price */}
                      <div className="col-span-2">
                        <form.Field name={`items[${index}].unitPrice`}>
                          {(subField: any) => (
                            <input
                              type="number"
                              className="flex h-9 w-full rounded-md border border-slate-200 bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 text-right"
                              value={subField.state.value}
                              onChange={(e) => {
                                const price = Number(e.target.value);
                                subField.handleChange(price);
                                const qty = Number(form.getFieldValue(`items[${index}].quantity`));
                                form.setFieldValue(`items[${index}].totalAmount`, qty * price);
                              }}
                            />
                          )}
                        </form.Field>
                      </div>

                      {/* Total Amount (Read-only) */}
                      <div className="col-span-1 text-right font-mono font-medium text-sm text-indigo-700">
                        <form.Subscribe selector={(s: any) => s.values.items[index]?.totalAmount}>
                          {(total: number) => formatNumber(total || 0)}
                        </form.Subscribe>
                      </div>

                      {/* Remove Button */}
                      <div className="col-span-1 flex justify-center">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            const current = form.getFieldValue("items");
                            form.setFieldValue("items", current.filter((_, i) => i !== index));
                          }}
                          disabled={field.state.value.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Ghi chú dòng — lưu trên dòng vật tư, in trên đơn mua gửi NCC */}
                      <div className="col-span-12">
                        <form.Field name={`items[${index}].notes`}>
                          {(subField: any) => (
                            <input
                              data-testid={`po-line-note-${index}`}
                              className="flex h-8 w-full rounded-md border border-dashed border-slate-200 bg-transparent px-3 py-1 text-xs placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950"
                              placeholder="Ghi chú dòng (quy cách, yêu cầu giao…) — in trên đơn mua"
                              value={subField.state.value ?? ""}
                              onChange={(e) => subField.handleChange(e.target.value)}
                            />
                          )}
                        </form.Field>
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Row Button */}
                  <div className="p-2 border-t bg-slate-50/50">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      onClick={() => {
                        const current = form.getFieldValue("items");
                        form.setFieldValue("items", [...current, { materialId: "", quantity: 1, unitPrice: 0, totalAmount: 0 }]);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Thêm vật tư
                    </Button>
                  </div>
                </div>
              )}
            </form.Field>
          </div>
          
          <div className="flex justify-end">
            <form.Subscribe selector={(s: any) => s.values.items}>
              {(items: any[]) => {
                const grandTotal = items.reduce((acc, curr) => acc + (Number(curr.totalAmount) || 0), 0);
                return (
                  <div className="text-right p-3 bg-indigo-50 rounded-lg border border-indigo-100 min-w-[250px]">
                    <div className="text-sm text-slate-500">Tổng cộng (VND)</div>
                    <div className="text-xl font-bold text-indigo-700">{formatNumber(grandTotal)} ₫</div>
                  </div>
                );
              }}
            </form.Subscribe>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4 text-slate-500" /> Thời gian & Trạng
            thái
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-1">
            <F
              form={form}
              name="orderDate"
              label="Ngày đặt"
              type="date"
              required
            />
          </div>
          <div className="col-span-1">
            <F
              form={form}
              name="expectedDate"
              label="Ngày dự kiến nhận"
              type="date"
            />
          </div>
          <form.Field name="notes">
            {(field: any) => (
              <div className="col-span-2 md:col-span-4 space-y-1.5 flex flex-col flex-1">
                <Label className="text-xs font-semibold text-slate-700 whitespace-normal">
                  Ghi chú
                </Label>
                <Textarea
                  value={field.state.value ?? ""}
                  placeholder="Ghi chú..."
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  rows={2}
                  className="min-h-9"
                />
              </div>
            )}
          </form.Field>
        </CardContent>
      </Card>

      <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 backdrop-blur-md bg-white/80 p-4 border-t flex justify-end gap-3 mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Huỷ
        </Button>
        <form.Subscribe selector={(s: any) => [s.canSubmit, s.isSubmitting]}>
          {([canSubmit, submitting]: any) => (
            <SaveActionButtons
              status={currentStatus}
              busy={Boolean(submitting || isSubmitting || isDraftSaving)}
              onSaveDraft={handleSaveDraft}
              submitLabel={submitLabel}
            />
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}
